import { JobProfitDetails, LabeledAmount } from '@/types';
import { withBackNavigation } from '@/lib/withBackNavigation';
import type { ModalLayer } from '@/lib/layerManager';
import { X, TrendingUp, ArrowDownCircle } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useCompanyTimezone } from '@/hooks/useGlobalCompany';
import { formatInstantInCompanyTimeZone } from '@/lib/dateUtils';

interface JobProfitDetailsModalProps {
    isOpen: boolean;
    onClose: () => void;
    profitDetails?: JobProfitDetails;
    layer?: ModalLayer;
}

function JobProfitDetailsModalBase({ isOpen, onClose, profitDetails, layer }: JobProfitDetailsModalProps) {
    if (!isOpen || !profitDetails) return null;

    const companyTz = useCompanyTimezone();

    // Format currency
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    };

    // Format date
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return 'N/A';
        return formatInstantInCompanyTimeZone(dateString, companyTz);
    };

    // Calculate totals for profit
    const totalProfitDeductions = profitDetails.deductions.reduce((sum: number, item: LabeledAmount) => sum + item.amount, 0);

    return createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4" style={layer?.style}>
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <TrendingUp className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Job Profit Details</h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {/* Job Total */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Job Revenue</h4>
                        <div className="flex items-center justify-between">
                            <span className="text-gray-600">Total collected</span>
                            <span className="text-lg font-semibold text-gray-900">
                                {formatCurrency(profitDetails.job_total)}
                            </span>
                        </div>
                    </div>

                    {/* Deductions */}
                    {profitDetails.deductions.length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <ArrowDownCircle className="h-4 w-4 text-red-500" />
                                Expenses & Deductions
                            </h4>
                            <div className="space-y-2">
                                {profitDetails.deductions.map((item: LabeledAmount, index: number) => (
                                    <div
                                        key={index}
                                        className="flex items-center justify-between py-2 px-3 bg-red-50 rounded-md"
                                    >
                                        <span className="text-sm text-gray-700">{item.label}</span>
                                        <span className="text-sm font-medium text-red-700">
                                            -{formatCurrency(item.amount)}
                                        </span>
                                    </div>
                                ))}

                                <div className="flex items-center justify-between py-2 px-3 border-t border-red-200">
                                    <span className="text-sm font-medium text-gray-700">Total Deductions</span>
                                    <span className="text-sm font-bold text-red-700">
                                        -{formatCurrency(totalProfitDeductions)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Final Profit */}
                    <div className={`border rounded-lg p-4 ${profitDetails.final_profit >= 0
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-red-50 border-red-200'
                        }`}>
                        <div className="flex items-center justify-between">
                            <span className="text-base font-medium text-gray-900">Final Job Profit</span>
                            <span className={`text-xl font-bold ${profitDetails.final_profit >= 0 ? 'text-blue-700' : 'text-red-700'
                                }`}>
                                {formatCurrency(profitDetails.final_profit)}
                            </span>
                        </div>
                    </div>

                    {/* Calculated timestamp */}
                    {profitDetails.calculated_at && (
                        <div className="text-xs text-gray-500 text-center">
                            Calculated on {formatDate(profitDetails.calculated_at)}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}
export const JobProfitDetailsModal = withBackNavigation(JobProfitDetailsModalBase);